import React, { useState, useEffect } from 'react';
import './App.css';
import API_BASE_URL from './config';

function ContactsList({ onViewContact, onBack, theme, toggleTheme, initialMode }) {
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState({});
  const [cities, setCities] = useState([]);
  const [contactTypeSettings, setContactTypeSettings] = useState({});
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddForm, setShowAddForm] = useState(initialMode === 'add');
  const [showSettingsForm, setShowSettingsForm] = useState(initialMode === 'settings');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sprint 16 Phase 1.4b: CSV Import/Export state
  const [showImportModal, setShowImportModal] = useState(initialMode === 'import');
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  
  // Sprint 16 Phase 1.4c: Import Wizard state
  const [importStep, setImportStep] = useState(1); // 1=upload, 2=preview, 3=results
  const [previewContacts, setPreviewContacts] = useState([]);
  const [bulkCity, setBulkCity] = useState('');
  const [bulkContactType, setBulkContactType] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    contact_type: '',
    phone: '',
    email: '',
    notes: '',
    associated_city: ''
  });

  useEffect(() => {
    fetchContacts();
    fetchSettings();
    fetchCities();
    fetchContactTypeSettings();
  }, [sortBy, sortOrder]);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contacts?sort_by=${sortBy}&sort_order=${sortOrder}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_settings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cities`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setCities(data);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchContactTypeSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_type_settings`, {
        credentials: 'include'
      });
      const data = await response.json();
      setContactTypeSettings(data);
    } catch (error) {
      console.error('Error fetching contact type settings:', error);
    }
  };

  const handleExportToExcel = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export/contacts`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `contacts_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Contacts exported to Excel successfully!');
    } catch (error) {
      console.error('Error exporting contacts:', error);
      alert('❌ Failed to export contacts. Please try again.');
    }
  };

  // Sprint 16 Phase 1.4b: CSV Export
  const handleExportToCSV = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/contacts/export/csv`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('CSV export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Contacts exported to CSV successfully!');
    } catch (error) {
      console.error('Error exporting contacts to CSV:', error);
      alert('❌ Failed to export contacts to CSV');
    }
  };

  // Sprint 16 Phase 1.4b: CSV Import
  // Sprint 16 Phase 1.4c: Parse CSV and show preview
  const handleImportCSV = async () => {
    if (!importFile) {
      alert('Please select a CSV file first');
      return;
    }

    setIsImporting(true);

    try {
      const text = await importFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        alert('CSV file is empty');
        setIsImporting(false);
        return;
      }

      // Parse CSV (simple parser, handles basic cases)
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const contacts = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= 3) { // At least name, email, phone
          const contact = {
            row: i + 1,
            name: values[0] || '',
            email: values[1] || '',
            phone: values[2] || '',
            company_name: values[3] || '',
            city: values[4] || '',
            contact_type: values[5] || '',
            notes: values[6] || ''
          };
          contacts.push(contact);
        }
      }

      if (contacts.length === 0) {
        alert('No valid contacts found in CSV file');
        setIsImporting(false);
        return;
      }

      setPreviewContacts(contacts);
      setImportStep(2); // Move to preview step
    } catch (error) {
      console.error('CSV parse error:', error);
      alert('Failed to parse CSV file: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Sprint 16 Phase 1.4c: Confirm and execute import
  const handleConfirmImport = async () => {
    setIsImporting(true);
    setImportResults(null);

    try {
      // Send preview contacts to backend
      const response = await fetch(`${API_BASE_URL}/contacts/import/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contacts: previewContacts })
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const results = await response.json();
      setImportResults(results);
      setImportStep(3); // Move to results step

      // Refresh contacts list if any were created
      if (results.created > 0) {
        fetchContacts();
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Sprint 16 Phase 1.4c: Update individual contact field
  const handlePreviewFieldChange = (index, field, value) => {
    const updated = [...previewContacts];
    updated[index][field] = value;
    setPreviewContacts(updated);
  };

  // Sprint 16 Phase 1.4c: Apply bulk assignments
  const handleBulkApply = (field) => {
    const value = field === 'city' ? bulkCity : bulkContactType;
    if (!value) {
      alert(`Please select a ${field} first`);
      return;
    }

    const updated = previewContacts.map(contact => ({
      ...contact,
      [field]: value
    }));
    setPreviewContacts(updated);
    
    // Clear bulk selection
    if (field === 'city') {
      setBulkCity('');
    } else {
      setBulkContactType('');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setImportFile(file);
      setImportResults(null);
    } else {
      alert('Please select a valid CSV file');
      e.target.value = null;
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResults(null);
    setImportStep(1);
    setPreviewContacts([]);
    setBulkCity('');
    setBulkContactType('');
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_BASE_URL}/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.error) {
        // Sprint 16 Phase 1.4a: Handle duplicate email error
        if (result.error === 'duplicate_email') {
          const viewExisting = window.confirm(
            `⚠️ Duplicate Email Detected\n\n` +
            `${result.message}\n\n` +
            `${result.suggestion}\n\n` +
            `Would you like to view the existing contact?`
          );
          if (viewExisting && result.existing_contact_id) {
            onViewContact(result.existing_contact_id);
          }
        } else {
          alert(result.error);
        }
      } else {
        setShowAddForm(false);
        setFormData({
          name: '',
          company_name: '',
          contact_type: '',
          phone: '',
          email: '',
          notes: '',
          associated_city: ''
        });
        fetchContacts();
      }
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Failed to add contact');
    }
  };

  const handleAddOption = async (settingKey, optionName) => {
    const newOption = prompt(`Add new ${optionName}:`);
    if (!newOption) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact_settings/${settingKey}/add_option`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ option: newOption })
      });
      
      const result = await response.json();
      
      if (result.error) {
        alert(result.error);
      } else {
        fetchSettings();
        alert(`${optionName} "${newOption}" added successfully!`);
      }
    } catch (error) {
      console.error('Error adding option:', error);
      alert('Failed to add option');
    }
  };

  const handleToggleCityLink = async (contactType, requiresCity) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_type_settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          contact_type: contactType, 
          requires_city_link: requiresCity 
        })
      });

      const result = await response.json();
      
      if (result.error) {
        alert(result.error);
      } else {
        fetchContactTypeSettings();
      }
    } catch (error) {
      console.error('Error updating city link setting:', error);
      alert('Failed to update setting');
    }
  };

  // Filter contacts by search term
  const filteredContacts = contacts.filter(contact => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (contact.name || '').toLowerCase().includes(searchLower) ||
      (contact.company_name || '').toLowerCase().includes(searchLower) ||
      (contact.email || '').toLowerCase().includes(searchLower) ||
      (contact.phone || '').toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button className="btn btn-outline-secondary me-3" onClick={onBack}>
            ← Back to RFQs
          </button>
          <h2 className="d-inline">📇 Contacts Directory</h2>
        </div>
        <div>
          <button className="btn btn-secondary me-2" onClick={toggleTheme}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button 
            className="btn btn-success me-2" 
            onClick={() => setShowAddForm(!showAddForm)}
          >
            ➕ Add Contact
          </button>
          <button 
            className="btn btn-primary me-2" 
            onClick={handleExportToExcel}
            title="Export all contacts to Excel"
          >
            📊 Export to Excel
          </button>
          <button 
            className="btn btn-info me-2" 
            onClick={handleExportToCSV}
            title="Export all contacts to CSV"
          >
            📄 Export CSV
          </button>
          <button 
            className="btn btn-warning me-2" 
            onClick={() => setShowImportModal(true)}
            title="Import contacts from CSV"
          >
            📥 Import CSV
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowSettingsForm(!showSettingsForm)}
          >
            ⚙️ Settings
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettingsForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Custom Dropdown Options</h5>
            <p className="text-muted">Add custom options to dropdown menus and configure city linking</p>
            
            <div className="row">
              <div className="col-md-4 mb-3">
                <strong>Contact Types:</strong>
                <ul className="list-unstyled ms-3 mb-2">
                  {(settings.contact_types || []).map((type, idx) => (
                    <li key={idx} className="d-flex align-items-center mb-1">
                      <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={contactTypeSettings[type] || false}
                          onChange={(e) => handleToggleCityLink(type, e.target.checked)}
                          title="Require city association for this contact type"
                          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        />
                        <span>• {type}</span>
                        {contactTypeSettings[type] && <small className="text-muted ms-2">(requires city)</small>}
                      </label>
                    </li>
                  ))}
                </ul>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleAddOption('contact_types', 'Contact Type')}
                >
                  + Add Type
                </button>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Capacity/Role Options:</strong>
                <ul className="list-unstyled ms-3 mb-2">
                  {(settings.capacity_options || []).map((cap, idx) => (
                    <li key={idx}>• {cap}</li>
                  ))}
                </ul>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleAddOption('capacity_options', 'Capacity/Role')}
                >
                  + Add Role
                </button>
              </div>
              
              <div className="col-md-4 mb-3">
                <strong>Interaction Types:</strong>
                <ul className="list-unstyled ms-3 mb-2">
                  {(settings.interaction_types || []).map((type, idx) => (
                    <li key={idx}>• {type}</li>
                  ))}
                </ul>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleAddOption('interaction_types', 'Interaction Type')}
                >
                  + Add Type
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Contact Form */}
      {showAddForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Add New Contact</h5>
            <form onSubmit={handleAddContact}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                
                <div className="col-md-6 mb-3">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Contact Type</label>
                  <select
                    className="form-select"
                    value={formData.contact_type}
                    onChange={(e) => setFormData({...formData, contact_type: e.target.value})}
                  >
                    <option value="">Select type...</option>
                    {(settings.contact_types || []).map((type, idx) => (
                      <option key={idx} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                {/* Conditional City Dropdown */}
                {formData.contact_type && contactTypeSettings[formData.contact_type] && (
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Associated City *</label>
                    <select
                      className="form-select"
                      value={formData.associated_city}
                      onChange={(e) => setFormData({...formData, associated_city: e.target.value})}
                      required
                    >
                      <option value="">Select city...</option>
                      {cities.map((city, idx) => (
                        <option key={idx} value={city.name}>{city.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                
                <div className="col-md-4 mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />
              </div>
              
              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary">Add Contact</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="🔍 Search contacts by name, company, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Sort Controls */}
      <div className="mb-3 d-flex gap-2 align-items-center">
        <label className="me-2">Sort by:</label>
        <select 
          className="form-select form-select-sm" 
          style={{width: 'auto'}}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="name">Name</option>
          <option value="company_name">Company</option>
          <option value="contact_type">Type</option>
          <option value="created_at">Date Added</option>
        </select>
        
        <button 
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
        >
          {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
        </button>
        
        <span className="ms-auto text-muted">
          {filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Contacts Table */}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Type</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Added</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  {searchTerm ? 'No contacts match your search' : 'No contacts yet. Add your first contact!'}
                </td>
              </tr>
            ) : (
              filteredContacts.map((contact) => (
                <tr 
                  key={contact.id} 
                  onClick={() => onViewContact(contact.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <strong>{contact.name}</strong>
                  </td>
                  <td>{contact.company_name || '-'}</td>
                  <td>
                    {contact.contact_type && (
                      <span className="badge bg-secondary">{contact.contact_type}</span>
                    )}
                  </td>
                  <td>{contact.phone || '-'}</td>
                  <td>
                    {contact.email ? (
                      <a 
                        href={`mailto:${contact.email}`} 
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contact.email}
                      </a>
                    ) : '-'}
                  </td>
                  <td className="text-muted">
                    {new Date(contact.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Sprint 16 Phase 1.4b: CSV Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050
        }}>
          <div className="card" style={{ maxWidth: importStep === 2 ? '95%' : '600px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                📥 Import Contacts from CSV
                {importStep === 2 && <small className="ms-2 text-muted">({previewContacts.length} contacts)</small>}
              </h5>
              <div className="btn-group btn-group-sm">
                <button className={`btn ${importStep === 1 ? 'btn-primary' : 'btn-outline-secondary'}`} disabled>1. Upload</button>
                <button className={`btn ${importStep === 2 ? 'btn-primary' : 'btn-outline-secondary'}`} disabled>2. Preview</button>
                <button className={`btn ${importStep === 3 ? 'btn-primary' : 'btn-outline-secondary'}`} disabled>3. Results</button>
              </div>
            </div>
            <div className="card-body">
              {/* STEP 1: UPLOAD */}
              {importStep === 1 && (
                <>
                  <div className="alert alert-info">
                    <strong>CSV Format:</strong> Your CSV file should have these columns in order:
                    <ul className="mb-0 mt-2">
                      <li><code>name</code> - Required</li>
                      <li><code>email</code> - Optional (checked for duplicates)</li>
                      <li><code>phone</code> - Optional</li>
                      <li><code>company_name</code> - Optional</li>
                      <li><code>city</code> - Optional (can assign/bulk assign in preview step)</li>
                      <li><code>contact_type</code> - Optional (can assign/bulk assign in preview step)</li>
                      <li><code>notes</code> - Optional</li>
                    </ul>
                    <small className="text-muted">💡 Tip: You can leave city/type empty in CSV and use the preview step to bulk assign them!</small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Select CSV File:</label>
                    <input 
                      type="file" 
                      className="form-control"
                      accept=".csv"
                      onChange={handleFileSelect}
                      disabled={isImporting}
                    />
                    {importFile && (
                      <small className="text-success">
                        ✓ Selected: {importFile.name}
                      </small>
                    )}
                  </div>

                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      className="btn btn-secondary" 
                      onClick={closeImportModal}
                      disabled={isImporting}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary" 
                      onClick={handleImportCSV}
                      disabled={!importFile || isImporting}
                    >
                      {isImporting ? 'Parsing...' : 'Next: Preview →'}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 2: PREVIEW & MAPPING */}
              {importStep === 2 && (
                <>
                  <div className="alert alert-warning mb-3">
                    <strong>⚠ Review & Assign:</strong> Please review the imported contacts below and optionally assign City and Contact Type using the dropdowns. You can assign individually or use "Bulk Apply" to set all at once.
                    <br/>
                    <small className="text-muted">Note: City is optional - leave blank for contractors serving multiple areas. Service area filtering coming in future update!</small>
                  </div>
                  
                  {/* Debug info - can remove after testing */}
                  {(settings.contact_types || []).length === 0 && (
                    <div className="alert alert-info mb-3">
                      <strong>ℹ️ No contact types found.</strong> Go to Settings (gear icon) to add contact types like "Project Manager", "Contractor", "Vendor", etc.
                    </div>
                  )}

                  {/* Bulk Assignment Controls */}
                  <div className="card mb-3 bg-light">
                    <div className="card-body">
                      <h6 className="card-title">Bulk Assignment</h6>
                      <div className="row">
                        <div className="col-md-5">
                          <label className="form-label">Bulk City:</label>
                          <select 
                            className="form-select form-select-sm"
                            value={bulkCity}
                            onChange={(e) => setBulkCity(e.target.value)}
                          >
                            <option value="">-- Select City --</option>
                            {cities.map(city => (
                              <option key={city.name} value={city.name}>{city.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                          <button 
                            className="btn btn-sm btn-success w-100"
                            onClick={() => handleBulkApply('city')}
                            disabled={!bulkCity}
                          >
                            Apply to All
                          </button>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Bulk Contact Type:</label>
                          <select 
                            className="form-select form-select-sm"
                            value={bulkContactType}
                            onChange={(e) => setBulkContactType(e.target.value)}
                          >
                            <option value="">-- Select Type --</option>
                            {(settings.contact_types || []).map((type, idx) => (
                              <option key={idx} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-1 d-flex align-items-end">
                          <button 
                            className="btn btn-sm btn-success w-100"
                            onClick={() => handleBulkApply('contact_type')}
                            disabled={!bulkContactType}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preview Table */}
                  <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                    <table className="table table-sm table-bordered">
                      <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Company</th>
                          <th style={{ width: '150px' }}>City <small className="text-muted">(optional)</small></th>
                          <th style={{ width: '150px' }}>Type <small className="text-muted">(optional)</small></th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewContacts.map((contact, idx) => (
                          <tr key={idx}>
                            <td className="text-muted">{contact.row}</td>
                            <td>{contact.name || <span className="text-danger">Missing!</span>}</td>
                            <td><small>{contact.email || '-'}</small></td>
                            <td><small>{contact.phone || '-'}</small></td>
                            <td><small>{contact.company_name || '-'}</small></td>
                            <td>
                              <select 
                                className="form-select form-select-sm"
                                value={contact.city}
                                onChange={(e) => handlePreviewFieldChange(idx, 'city', e.target.value)}
                              >
                                <option value="">-- Select --</option>
                                {cities.map(city => (
                                  <option key={city.name} value={city.name}>{city.name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select 
                                className="form-select form-select-sm"
                                value={contact.contact_type}
                                onChange={(e) => handlePreviewFieldChange(idx, 'contact_type', e.target.value)}
                              >
                                <option value="">-- Select --</option>
                                {(settings.contact_types || []).map((type, idx) => (
                                  <option key={idx} value={type}>{type}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setImportStep(1)}
                    >
                      ← Back
                    </button>
                    <button 
                      className="btn btn-success" 
                      onClick={handleConfirmImport}
                      disabled={isImporting}
                    >
                      {isImporting ? 'Importing...' : 'Confirm Import →'}
                    </button>
                  </div>
                </>
              )}

              {/* STEP 3: RESULTS */}
              {importStep === 3 && importResults && (
                <>
                  <h6 className="mb-3">Import Results:</h6>
                  <div className="alert alert-success">
                    <strong>✓ Import Complete!</strong>
                    <ul className="mb-0 mt-2">
                      <li>Total rows: {importResults.total_rows}</li>
                      <li><strong className="text-success">{importResults.created} contacts created</strong></li>
                      <li>{importResults.skipped} rows skipped</li>
                    </ul>
                  </div>

                  {importResults.duplicates && importResults.duplicates.length > 0 && (
                    <div className="alert alert-warning">
                      <strong>⚠ Duplicate Emails Found ({importResults.duplicates.length}):</strong>
                      <ul className="mb-0 mt-2" style={{ maxHeight: '150px', overflow: 'auto' }}>
                        {importResults.duplicates.map((dup, idx) => (
                          <li key={idx}>
                            Row {dup.row}: {dup.name} ({dup.email}) - matches existing contact "{dup.existing_name}"
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResults.errors && importResults.errors.length > 0 && (
                    <div className="alert alert-danger">
                      <strong>❌ Errors ({importResults.errors.length}):</strong>
                      <ul className="mb-0 mt-2" style={{ maxHeight: '150px', overflow: 'auto' }}>
                        {importResults.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      className="btn btn-primary" 
                      onClick={closeImportModal}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContactsList;

